import React, { useState } from "react";
import axios from "axios";
import { Send, CheckCircle2 } from "lucide-react";
import { API } from "../config";

export default function ContactForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to send message. Please try again or email orders@carepointuk.net directly.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div
        className="bg-green-50 border border-green-200 rounded-lg p-6 text-center"
        data-testid="contact-form-success"
      >
        <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-3" />
        <h3 className="font-semibold text-gray-900 text-lg mb-1">Thank you — your message is on its way</h3>
        <p className="text-sm text-gray-700">
          We've received your enquiry and a confirmation copy has been sent to your inbox.
          Our team typically responds within one working day.
        </p>
        <button
          onClick={() => setSent(false)}
          className="mt-4 text-sm text-[#00a99d] hover:underline font-medium"
          data-testid="contact-form-send-another"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="contact-form">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Your name *</label>
        <input
          required
          type="text"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00a99d]"
          placeholder="Jane Smith"
          data-testid="contact-name-input"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Email address *</label>
        <input
          required
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00a99d]"
          placeholder="jane@yourcompany.co.uk"
          data-testid="contact-email-input"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Phone (optional)</label>
        <input
          type="tel"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00a99d]"
          placeholder="020 1234 5678"
          data-testid="contact-phone-input"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
        <input
          required
          type="text"
          value={form.subject}
          onChange={(e) => setForm({ ...form, subject: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00a99d]"
          placeholder="Bulk quote enquiry"
          data-testid="contact-subject-input"
        />
      </div>
      <div className="md:col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
        <textarea
          required
          rows={5}
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00a99d]"
          placeholder="Tell us how we can help…"
          data-testid="contact-message-input"
        />
      </div>
      {error && (
        <div className="md:col-span-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md p-3" data-testid="contact-form-error">
          {error}
        </div>
      )}
      <div className="md:col-span-2">
        <button
          type="submit"
          disabled={sending}
          className="inline-flex items-center gap-2 bg-[#00a99d] hover:bg-[#008c82] disabled:bg-gray-400 text-white font-semibold px-6 py-3 rounded-lg transition"
          data-testid="contact-form-submit"
        >
          <Send className="w-4 h-4" />
          {sending ? "Sending…" : "Send message"}
        </button>
        <p className="text-xs text-gray-500 mt-3">
          By submitting this form you consent to Carepoint Health Supplies storing your details to respond to your enquiry.
          We never share your data with third parties.
        </p>
      </div>
    </form>
  );
}
